import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useKegiatan, useCreateKegiatan, useUpdateKegiatan } from '@/hooks/useKegiatan'
import pb from '@/lib/pocketbase'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Select from '@/components/ui/Select'
import Card from '@/components/ui/Card'

const kegiatanSchema = z.object({
  judul: z.string().min(1, 'Judul harus diisi'),
  tema: z.string().optional(),
  jenis: z.enum(['semper', 'seminar', 'pelatihan', 'musyawarah', 'ribut', 'lainnya']),
  tanggal_mulai: z.string().min(1, 'Tanggal mulai harus diisi'),
  tanggal_selesai: z.string().min(1, 'Tanggal selesai harus diisi'),
  lokasi: z.string().min(1, 'Lokasi harus diisi'),
  target_peserta: z.coerce.number().min(1, 'Minimal 1 peserta'),
  deskripsi: z.string().optional(),
  latar_belakang: z.string().optional(),
  maksud_tujuan: z.string().optional(),
  status: z.enum(['draft', 'aktif', 'selesai', 'batal']).optional(),
})

type KegiatanFormData = z.infer<typeof kegiatanSchema>

const jenisOptions = [
  { label: 'Semper', value: 'semper' },
  { label: 'Seminar', value: 'seminar' },
  { label: 'Pelatihan', value: 'pelatihan' },
  { label: 'Musyawarah', value: 'musyawarah' },
  { label: 'Rapat', value: 'ribut' },
  { label: 'Lainnya', value: 'lainnya' },
]

const statusOptions = [
  { label: 'Draft', value: 'draft' },
  { label: 'Aktif', value: 'aktif' },
  { label: 'Selesai', value: 'selesai' },
  { label: 'Batal', value: 'batal' },
]

export default function KegiatanFormPage() {
  const { id: editId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = !!editId

  const { data: existing } = useKegiatan(editId)
  const createMutation = useCreateKegiatan()
  const updateMutation = useUpdateKegiatan()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<KegiatanFormData>({
    resolver: zodResolver(kegiatanSchema),
    defaultValues: {
      status: 'draft',
      jenis: 'seminar',
    },
  })

  React.useEffect(() => {
    if (existing && isEditing) {
      setValue('judul', existing.judul)
      setValue('tema', existing.tema || '')
      setValue('jenis', existing.jenis as KegiatanFormData['jenis'])
      setValue('tanggal_mulai', existing.tanggal_mulai.split(' ')[0])
      setValue('tanggal_selesai', existing.tanggal_selesai.split(' ')[0])
      setValue('lokasi', existing.lokasi)
      setValue('target_peserta', existing.target_peserta)
      setValue('deskripsi', existing.deskripsi || '')
      setValue('latar_belakang', existing.latar_belakang || '')
      setValue('maksud_tujuan', existing.maksud_tujuan || '')
      setValue('status', existing.status as KegiatanFormData['status'])
    }
  }, [existing, isEditing, setValue])

  const onSubmit = async (data: KegiatanFormData) => {
    try {
      const payload = {
        ...data,
        created_by: pb.authStore.model?.id || '',
      }
      if (isEditing) {
        await updateMutation.mutateAsync({ id: editId, data: payload })
        navigate(`/kegiatan/${editId}`)
      } else {
        const created = await createMutation.mutateAsync(payload)
        navigate(`/kegiatan/${created.id}`)
      }
    } catch (err) {
      console.error('Submit error:', err)
    }
  }

  const onSaveDraft = async (data: KegiatanFormData) => {
    await onSubmit({ ...data, status: 'draft' })
  }

  const onSavePublish = async (data: KegiatanFormData) => {
    await onSubmit({ ...data, status: 'aktif' })
  }

  const loading = createMutation.isPending || updateMutation.isPending

  return (
    <div className="py-4 space-y-4">
      <Card>
        <h2 className="text-base font-bold mb-4">{isEditing ? 'Edit Kegiatan' : 'Buat Kegiatan Baru'}</h2>

        <form className="space-y-4">
          <Input
            label="Judul Kegiatan *"
            placeholder="Contoh: Seminar Dakwah Nusantara"
            error={errors.judul?.message}
            {...register('judul')}
          />

          <Input
            label="Tema"
            placeholder="Tema kegiatan (opsional)"
            {...register('tema')}
          />

          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Jenis Kegiatan *"
              options={jenisOptions}
              {...register('jenis')}
            />
            <Select
              label="Status"
              options={statusOptions}
              {...register('status')}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Tanggal Mulai *"
              type="date"
              error={errors.tanggal_mulai?.message}
              {...register('tanggal_mulai')}
            />
            <Input
              label="Tanggal Selesai *"
              type="date"
              error={errors.tanggal_selesai?.message}
              {...register('tanggal_selesai')}
            />
          </div>

          <Input
            label="Lokasi *"
            placeholder="Contoh: Aula Masjid Al-Hidayah, Surabaya"
            error={errors.lokasi?.message}
            {...register('lokasi')}
          />

          <Input
            label="Target Peserta *"
            type="number"
            min={1}
            placeholder="100"
            error={errors.target_peserta?.message}
            {...register('target_peserta')}
          />

          <div>
            <label className="block text-sm font-semibold text-text-primary mb-1.5">Deskripsi</label>
            <textarea
              className="w-full border border-gray-300 rounded-input px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              rows={3}
              placeholder="Deskripsi kegiatan..."
              {...register('deskripsi')}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-text-primary mb-1.5">Latar Belakang</label>
            <textarea
              className="w-full border border-gray-300 rounded-input px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              rows={3}
              placeholder="Latar belakang kegiatan..."
              {...register('latar_belakang')}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-text-primary mb-1.5">Maksud & Tujuan</label>
            <textarea
              className="w-full border border-gray-300 rounded-input px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              rows={3}
              placeholder="Maksud dan tujuan kegiatan..."
              {...register('maksud_tujuan')}
            />
          </div>

          <div className="pt-2 space-y-2">
            <Button
              type="button"
              block
              variant="outline"
              loading={loading}
              onClick={handleSubmit(onSaveDraft)}
            >
              Simpan sebagai Draft
            </Button>
            <Button
              type="button"
              block
              loading={loading}
              onClick={handleSubmit(onSavePublish)}
            >
              Simpan dan Publish
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
